import useDiscoveryFetch from './useDiscoveryFetch';
import { useRef, useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Form, Row, Col, Card } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import COUNTRY_COORDINATES from '../../config/countryCoordinates';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryCountryCard from './MobileDiscoveryCountryCard';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import icFilter from '../../assets/img/ic-filter.svg';
import icJobSearch from '../../assets/img/ic-job-search.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const getCountryCoords = (countryItem) => {
    const iso2 = String(countryItem.country?.id || countryItem.iso2 || countryItem.country || '').toUpperCase();
    return COUNTRY_COORDINATES[iso2] || null;
};

const PAGE_DISCOVERY_TYPE = 'countries';

const DiscoverCountryList = () => {
    const navigate = useNavigate();
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [countriesList, setCountriesList] = useState([]);
    const [sortOrderCountry, setSortOrderCountry] = useState('asc');
    const [sortOrderCities, setSortOrderCities] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [countriesPerPage, setCountriesPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [selectedContinentAllocation, setSelectedContinentAllocation] = useState('');
    const [selectedLanguagesSpoken, setSelectedLanguagesSpoken] = useState([]);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    const [favorites, setFavorites] = useState(new Set());

    const continentAllocationOptions = [
        ...new Set(
            countriesList
                .map((countryItem) => String(countryItem.continent || '').trim())
                .filter(Boolean)
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((continent) => ({
            value: continent,
            label: continent,
        }));

    const languagesSpokenOptions = [
        ...new Set(
            countriesList.flatMap((countryItem) =>
                Array.isArray(countryItem.languagesSpoken)
                    ? countryItem.languagesSpoken
                        .map((language) => String(language || '').trim())
                        .filter(Boolean)
                    : []
            )
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((language) => ({
            value: language,
            label: language,
        }));

    const filteredCountries = countriesList.filter((countryItem) => {
        if (!countryItem.cityCount) return false;

        const matchesSearch =
            !searchQuery ||
            String(countryItem.countryName || countryItem.country?.name || countryItem.country || '')
                .toLowerCase()
                .includes(searchQuery.toLowerCase());

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(countryItem._id));

        const matchesContinentAllocation =
            !selectedContinentAllocation ||
            String(countryItem.continent || '').trim() === selectedContinentAllocation;

        const matchesLanguagesSpoken =
            !Array.isArray(selectedLanguagesSpoken) ||
            selectedLanguagesSpoken.length === 0 ||
            (
                Array.isArray(countryItem.languagesSpoken) &&
                selectedLanguagesSpoken.some((selectedLanguage) =>
                    countryItem.languagesSpoken.includes(selectedLanguage)
                )
            );

        return (
            matchesSearch &&
            matchesFavorites &&
            matchesContinentAllocation &&
            matchesLanguagesSpoken
        );
    });

    const indexOfLastCountry = currentPage * countriesPerPage;
    const indexOfFirstCountry = indexOfLastCountry - countriesPerPage;
    const currentCountries = filteredCountries.slice(indexOfFirstCountry, indexOfLastCountry);

    const selectedLanguagesSpokenKey = useMemo(
        () => selectedLanguagesSpoken.join('|'),
        [selectedLanguagesSpoken]
    );

    const mobileFilterKey = JSON.stringify({
        searchQuery,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpoken,
        sortOrderCountry,
        sortOrderCities,
    });
    const mobilePagination = useMobileLoadMore({
        items: filteredCountries,
        filterKey: mobileFilterKey,
        storageKey: 'discovery-countries-mobile-list',
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpokenKey,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpoken,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
        setSelectedContinentAllocation(filters.selectedContinentAllocation || '');
        setSelectedLanguagesSpoken(
            Array.isArray(filters.selectedLanguagesSpoken)
                ? filters.selectedLanguagesSpoken
                : []
        );
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;

            const payload = {
                page: 'countries',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };

            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;

            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );

                return [savedFilterSet, ...withoutDuplicate];
            });

            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery country filter set:', error);
        }
    };

    // preserveSearch: mobile deletes a saved preset without touching the live
    // toolbar search, since Search is independent of staged/saved filter state there.
    // Desktop keeps the previous full-reset behavior.
    const handleDeleteFilterSet = async (filterSetId, { preserveSearch = false } = {}) => {
        if (!filterSetId || filterSetId === 'default') return;

        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);

            const wasSelected = String(selectedFilterSet) === String(filterSetId);

            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );

            if (wasSelected) {
                setSelectedFilterSet('default');
                if (!preserveSearch) setSearchQuery('');
                setSelectedFavorites?.(null);
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);

        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }

        const selectedSavedFilterSet = savedFilterSets.find(
            (item) => item._id === value
        );

        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    const clearMobileFiltersPreserveSearch = () => {
        setSelectedFavorites(null);
        setSelectedContinentAllocation('');
        setSelectedLanguagesSpoken([]);
    };

    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
    };

    useEffect(() => {
        trackEvent(ANALYTICS_EVENTS.DISCOVERY_OPENED, {
            discoveryType: 'countries',
            source: 'top_level_discovery',
            level: 1,
        });
    }, []);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchCountriesAndFilterSets = async () => {
            try {
                const [countriesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get('/api/discovery/countries', { signal }),
                    api.get('/api/discovery-filter-sets?page=countries', { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const safeCountries = Array.isArray(countriesRes.data) ? countriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCountries = Array.isArray(currentUserRes?.data?.favoriteCountries)
                    ? currentUserRes.data.favoriteCountries.map((id) => String(id))
                    : [];

                setCountriesList(refreshSortRef.current ? [...safeCountries].sort(refreshSortRef.current) : safeCountries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCountries));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching discovery countries / filter sets / favorites:', error);
            }
        };

        fetchCountriesAndFilterSets();
    }, []);

    const sortCountriesByCountry = () => {
        const newSortOrder = sortOrderCountry === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const countryA = String(a.countryName || a.country || '').toLowerCase();
            const countryB = String(b.countryName || b.country || '').toLowerCase();

            return newSortOrder === 'asc'
                ? countryA.localeCompare(countryB)
                : countryB.localeCompare(countryA);
        };
        const sortedCountries = [...countriesList].sort(refreshSortRef.current);

        setCountriesList(sortedCountries);
        setSortOrderCountry(newSortOrder);
    };

    const sortCountriesByCities = () => {
        const newSortOrder = sortOrderCities === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const countA = Number(a.cityCount) || 0;
            const countB = Number(b.cityCount) || 0;

            return newSortOrder === 'asc' ? countA - countB : countB - countA;
        };
        const sortedCountries = [...countriesList].sort(refreshSortRef.current);

        setCountriesList(sortedCountries);
        setSortOrderCities(newSortOrder);
    };

    const totalPages = Math.ceil(filteredCountries.length / countriesPerPage);
    const noCountries = filteredCountries.length === 0;

    const countriesWithCoordinates = filteredCountries.filter((countryItem) => getCountryCoords(countryItem) !== null);

    const anyCountriesWithCoordinates = countriesList.some((countryItem) => getCountryCoords(countryItem) !== null);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;

        if (totalPages <= 1) return [1];

        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };

        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(
            Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
            totalPages
        );

        const siblingsStart = Math.max(
            Math.min(
                currentPage - siblingCount,
                totalPages - boundaryCount - siblingCount * 2 - 1
            ),
            boundaryCount + 2
        );

        const siblingsEnd = Math.min(
            Math.max(
                currentPage + siblingCount,
                boundaryCount + siblingCount * 2 + 2
            ),
            totalPages - boundaryCount - 1
        );

        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2
                ? ['ellipsis']
                : boundaryCount + 1 < totalPages - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1
                ? ['ellipsis']
                : totalPages - boundaryCount > boundaryCount
                    ? [totalPages - boundaryCount]
                    : []),
            ...endPages,
        ];

        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (countryId) => {
        const countryIdStr = String(countryId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(countryIdStr);

        const optimisticFavorites = new Set(previousFavorites);

        if (wasFavorited) {
            optimisticFavorites.delete(countryIdStr);
        } else {
            optimisticFavorites.add(countryIdStr);
        }

        setFavorites(optimisticFavorites);

        try {
            const res = await api.patch(`/api/users/me/favorite-countries/${countryIdStr}`);

            const safeFavoriteCountries = Array.isArray(res?.data?.favoriteCountries)
                ? res.data.favoriteCountries.map((id) => String(id))
                : [];

            setFavorites(new Set(safeFavoriteCountries));
        } catch (error) {
            console.error('Error toggling favorite country:', error);
            setFavorites(previousFavorites);
        }
    };

    const discoveryNavItems = [
        { to: '/discovery/countries', eventKey: 'countries', label: 'Countries' },
        { to: '/discovery/cities', eventKey: 'cities', label: 'Cities' },
        { to: '/discovery/companies/jobs', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: ['/discovery/companies', '/discovery/jobs'] },
    ];

    // Countries has no city/industry data and no student/internship/english-speaking
    // signal of its own — those are company attributes. Route those to Companies instead
    // of pretending Countries can filter on them.
    const isCompanySpecificFilter = (sf) =>
        Boolean(sf?.city) ||
        Boolean(sf?.industry) ||
        sf?.jobType === 'student' ||
        sf?.jobType === 'english-speaking' ||
        sf?.jobType === 'internship';

    const getCountryIso2ByName = (countryName) => {
        if (!countryName) return '';

        const normalizedName = String(countryName).trim().toLowerCase();
        const match = countriesList.find(
            (countryItem) => String(countryItem.countryName || '').trim().toLowerCase() === normalizedName
        );

        return match ? match.country : '';
    };

    // Supported locally on Countries: country -> searchQuery, language -> selectedLanguagesSpoken.
    const applyStructuredFilters = (sf) => {
        if (!sf) return;

        if (sf.country) {
            setSearchQuery(sf.country);
        }

        if (sf.language) {
            setSelectedLanguagesSpoken((prev) =>
                prev.includes(sf.language) ? prev : [...prev, sf.language]
            );
        }
    };

    const navigateToCompaniesWithFilters = (sf) => {
        const params = new URLSearchParams();

        if (sf.city) params.set('city', sf.city);
        if (sf.industry) params.set('industry', sf.industry);
        if (sf.jobType === 'student') params.set('studentFriendly', 'true');
        if (sf.jobType === 'internship') params.set('internship', 'true');
        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            params.set('englishSpeaking', 'true');
        }

        const countryIso2 = getCountryIso2ByName(sf.country);
        if (countryIso2) params.set('country', countryIso2);

        navigate(`/discovery/companies?${params.toString()}`);
    };

    const handleChipClick = (sf) => {
        if (!sf) return;

        if (isCompanySpecificFilter(sf)) {
            navigateToCompaniesWithFilters(sf);
            return;
        }

        applyStructuredFilters(sf);
    };

    // Opt this page into the shared mobile AI assistant (header trigger +
    // bottom sheet). No helper-supported filters here, so suggestions are the
    // generic Discovery fallback set.
    useMobileAiPageContext(
        {
            pageType: 'discovery-countries',
            mapFirst: true,
            mapScope: 'global',
            discoveryType: PAGE_DISCOVERY_TYPE,
            suggestions: getDiscoveryAiSuggestions({ discoveryType: PAGE_DISCOVERY_TYPE }),
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-countries-page discovery-map-page${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>Discover</h1>
                                        </Col>
                                    </Row>

                                    {anyCountriesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={countriesWithCoordinates}
                                                        height={260}
                                                        singleItemZoom={4.5}
                                                        compactSpreadZoom={true}
                                                        spreadZoomOffset={-1}
                                                        clusterPixelRadius={0}
                                                        markerScale={0.6}
                                                        markerClassName="discovery-map-marker--country"
                                                        getLatitude={(countryItem) => getCountryCoords(countryItem)?.lat}
                                                        getLongitude={(countryItem) => getCountryCoords(countryItem)?.lng}
                                                        getTitle={(countryItem) => countryItem.countryName || countryItem.country?.name || countryItem.country}
                                                        getSubtitle={(countryItem) => countryItem.continent || ''}
                                                        getLink={(countryItem) => `/discovery/countries/${countryItem.country}`}
                                                        ctaLabel="Explore country →"
                                                        getSecondaryLink={(countryItem) => `/discovery/countries/${countryItem.country}/cities`}
                                                        secondaryCtaLabel="View cities →"
                                                        getTertiaryLink={(countryItem) => `/discovery/countries/${countryItem.country}/companies`}
                                                        tertiaryCtaLabel="View companies →"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mb-4 discovery-tabs-row">
                                        <Col>
                                            <NavTabs
                                                items={discoveryNavItems}
                                                className="discovery-route-tabs"
                                                autoRevealActive
                                                trackOverflow
                                            />
                                        </Col>
                                    </Row>

                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Country"
                                                    variant="countries"
                                                    items={countriesList}
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    selectedContinentAllocation={selectedContinentAllocation}
                                                    setSelectedContinentAllocation={setSelectedContinentAllocation}
                                                    continentAllocationOptions={continentAllocationOptions}
                                                    selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                    setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                    languagesSpokenOptions={languagesSpokenOptions}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryHeading title="Countries" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search countries"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                ]}
                                            />

                                            <DiscoveryFilterBar
                                                title="Country"
                                                variant="countries"
                                                items={countriesList}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                selectedFavorites={selectedFavorites}
                                                setSelectedFavorites={setSelectedFavorites}
                                                selectedContinentAllocation={selectedContinentAllocation}
                                                setSelectedContinentAllocation={setSelectedContinentAllocation}
                                                continentAllocationOptions={continentAllocationOptions}
                                                selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                languagesSpokenOptions={languagesSpokenOptions}
                                                savedFilterSets={savedFilterSets}
                                                selectedFilterSet={selectedFilterSet}
                                                onFilterSetChange={handleFilterSetChange}
                                                onDeleteFilterSet={(filterSetId) => handleDeleteFilterSet(filterSetId, { preserveSearch: true })}
                                                onSaveFilters={handleSaveFilters}
                                                onClearFilters={clearMobileFiltersPreserveSearch}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={() => setShowMobileFilters(false)}
                                                mobileModalTitle="Filter countries"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {noCountries && isMobile ? (
                                        <DiscoveryEmptyState
                                            className="discovery-empty-state--mobile-jobs"
                                            icon={icJobSearch}
                                            title="No matching countries found"
                                            description="Try removing one or more filters or adjust your search."
                                            primaryAction={{
                                                label: 'Clear search & filters',
                                                variant: 'primary',
                                                onClick: clearEmptyStateSearchAndFilters,
                                            }}
                                            secondaryAction={{
                                                label: 'Adjust filters',
                                                variant: 'outline-secondary',
                                                onClick: () => setShowMobileFilters(true),
                                            }}
                                        />
                                    ) : noCountries ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No countries available.</p>
                                            </Col>
                                        </Row>
                                    ) : isMobile ? (
                                        <div className="mobile-company-list">
                                            {mobilePagination.mobileItems.map((countryItem) => (
                                                <MobileDiscoveryCountryCard
                                                    key={countryItem._id}
                                                    country={countryItem}
                                                    isFavorite={favorites.has(String(countryItem._id))}
                                                    onFavoriteToggle={toggleFavorite}
                                                    onOpenCountry={(selectedCountry) => {
                                                        mobilePagination.rememberScrollPosition();
                                                        navigate(`/discovery/countries/${selectedCountry.country}`);
                                                    }}
                                                />
                                            ))}
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="countries"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table discovery-table--countries">
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>

                                                                <th onClick={sortCountriesByCountry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Country
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th>Continent</th>

                                                                <th onClick={sortCountriesByCities} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Cities
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th>Languages</th>

                                                                <th>Favorite</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCountries.map((countryItem) => (
                                                                <tr
                                                                    className="company-row discovery-row"
                                                                    key={countryItem._id}
                                                                    onClick={() => navigate(`/discovery/countries/${countryItem.country}`)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {/* ISO2 */}
                                                                    <td>{countryItem.country || '—'}</td>

                                                                    {/* Country name */}
                                                                    <td>
                                                                        {countryItem.countryName || countryItem.country?.name || '—'}
                                                                    </td>

                                                                    {/* Continent */}
                                                                    <td>{countryItem.continent || '—'}</td>

                                                                    {/* Cities */}
                                                                    <td>{countryItem.cityCount || 0}</td>

                                                                    {/* Languages */}
                                                                    <td>
                                                                        {Array.isArray(countryItem.languagesSpoken) && countryItem.languagesSpoken.length > 0
                                                                            ? countryItem.languagesSpoken.join(', ')
                                                                            : '—'}
                                                                    </td>

                                                                    {/* Favorite */}
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(countryItem._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(countryItem._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && (
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCountries.length} {filteredCountries.length === 1 ? 'country' : 'countries'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>

                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}

                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={countriesPerPage}
                                                    onChange={(e) => {
                                                        setCountriesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>

                                            <Form.Control
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default DiscoverCountryList;
